import { Injectable, inject } from '@angular/core';
import { Firestore, collection, doc, addDoc, updateDoc, getDoc, onSnapshot, Unsubscribe, setDoc, getDocs, query, orderBy, where, deleteDoc, deleteField, runTransaction, arrayRemove, arrayUnion, QueryConstraint } from '@angular/fire/firestore';
import { Observable, BehaviorSubject } from 'rxjs';
import { Workshop, WorkshopModule, WorkshopActivity, ActivityStep, WorkshopActiveActivityRef, WorkshopLastCompletedActivityRef, WorkshopReference, ActivityResponse } from 'src/app/core/schema';
import { WatchedModuleEntry, UserWatchedModules, WorkshopModuleStatus } from 'src/app/domain/user/types/User.model';
import { AccountState } from 'src/app/store/account/account.state';

/**
 * Manages workshop content and user progress for the Academy feature.
 * Handles real-time Firestore listeners for workshop, module, and activity data.
 * Firestore: `workshops/{id}`, `users/{email}/watchedModules`
 * @feature academy
 */
@Injectable({
  providedIn: 'root'
})
export class WorkshopService {
  private workshopSubject = new BehaviorSubject<Workshop | null>(null);
  public workshop$ = this.workshopSubject.asObservable();
  
  private unsubscribe: Unsubscribe | null = null;

  private readonly accountState = inject(AccountState);

  constructor(
    private firestore: Firestore,
  ) {}

  /**
   * Create a new workshop session
   */
  /**
   * @deprecated Use createWorkshopV2 instead
   */
  async createWorkshop(request: Record<string, unknown>): Promise<string> {
    throw new Error('createWorkshop is deprecated. Use createWorkshopV2 instead.');
  }

  /**
   * Create a new workshop session (V2 - using new Workshop model)
   * Creates both the workshop document and reference documents for invited participants
   * @param workshop - Complete workshop object (without id)
   * @param partnerId - Optional partner ID (defaults to active team)
   * @returns Workshop ID
   */
  async createWorkshopV2(workshop: Omit<Workshop, 'id'>, partnerId?: string): Promise<string> {
    // Get partner ID (use provided or active team)
    const partnerIdToUse = partnerId || (this.accountState.activeTeam()?.id ?? null);
    if (!partnerIdToUse) {
      throw new Error('No active partner/team found');
    }


    // Create workshop in partner's workshops collection
    const workshopsCollection = collection(
      this.firestore,
      `partners/${partnerIdToUse}/workshops`
    );
    const docRef = await addDoc(workshopsCollection, {
      ...workshop,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
    
    const workshopId = docRef.id;

    // Create reference documents for all invited participants
    await this.createWorkshopReferencesForParticipants(
      partnerIdToUse,
      workshopId,
      workshop
    );

    return workshopId;
  }

  /**
   * Load all workshops from Firestore
   * @returns Array of workshops with their IDs
   */
  async loadAllWorkshops(): Promise<Workshop[]> {
    try {
      const workshopsCollection = collection(this.firestore, 'workshops');
      const q = query(workshopsCollection);
      const querySnapshot = await getDocs(q);
      
      const workshops: Workshop[] = [];
      querySnapshot.forEach((doc) => {
        workshops.push({
          id: doc.id,
          ...doc.data() as Omit<Workshop, 'id'>
        });
      });
      
      return workshops;
    } catch (error) {
      console.error('[WorkshopService] Error loading workshops:', error);
      return [];
    }
  }

  /**
   * Load all workshops for a specific partner
   * Used by admins to view all workshops in their district
   * @param partnerId - Partner ID
   * @returns Array of workshops with their IDs
   */
  async loadAllWorkshopsForPartner(partnerId: string, year?: number): Promise<Workshop[]> {
    try {
      const workshopsCollection = collection(
        this.firestore,
        `partners/${partnerId}/workshops`
      );
      
      // If year is provided, filter by it
      let q;
      if (year) {
        // Get start and end timestamps for the year
        const startOfYear = new Date(year, 0, 1).getTime(); // Jan 1, YYYY 00:00:00
        const endOfYear = new Date(year + 1, 0, 1).getTime(); // Jan 1, YYYY+1 00:00:00
        
        
        // Filter by createdAt within the year range
        q = query(
          workshopsCollection,
          where('createdAt', '>=', startOfYear),
          where('createdAt', '<', endOfYear),
          orderBy('createdAt', 'desc')
        );
      } else {
        // Load all workshops
        q = query(workshopsCollection, orderBy('createdAt', 'desc'));
      }
      
      const querySnapshot = await getDocs(q);
      
      const workshops: Workshop[] = [];
      querySnapshot.forEach((doc) => {
        workshops.push({
          id: doc.id,
          ...doc.data() as Omit<Workshop, 'id'>
        });
      });
      
      const yearFilter = year ? ` for year ${year}` : '';
      return workshops;
    } catch (error) {
      console.error('[WorkshopService] Error loading workshops for partner:', error);
      return [];
    }
  }

  /**
   * Join an existing workshop
   */
  async joinWorkshop(workshopIdOrParams: string | { workshopId: string; userEmail: string }, userEmail?: string): Promise<boolean | void> {
    let workshopId: string;
    let email: string;
    
    // Handle both call signatures
    if (typeof workshopIdOrParams === 'string') {
      workshopId = workshopIdOrParams;
      email = userEmail!;
    } else {
      workshopId = workshopIdOrParams.workshopId;
      email = workshopIdOrParams.userEmail;
    }

    try {
      const workshopRef = doc(this.firestore, 'workshops', workshopId);
      const workshopDoc = await getDoc(workshopRef);

      if (!workshopDoc.exists()) {
        throw new Error('Workshop not found');
      }

      const workshop = workshopDoc.data() as Workshop;

      // Add user to connectedParticipants if not already in
      const connectedParticipants = workshop.connectedParticipants || [];
      const updateData: Record<string, unknown> = {
        [`participantJoinedAt.${email}`]: Date.now(),
      };
      if (!connectedParticipants.includes(email)) {
        updateData['connectedParticipants'] = [...connectedParticipants, email];
      }
      await updateDoc(workshopRef, updateData);
      
      // Return true if called with params object
      if (typeof workshopIdOrParams === 'object') {
        return true;
      }
    } catch (error) {
      if (typeof workshopIdOrParams === 'object') {
        return false;
      }
      throw error;
    }
  }

  /**
   * Heartbeat configuration
   */
  private readonly HEARTBEAT_INTERVAL = 90000; // 90 seconds (1.5 minutes)
  private readonly DISCONNECT_THRESHOLD = 270000; // 270 seconds (4.5 minutes = 3 missed heartbeats)

  /**
   * Send heartbeat signal to indicate participant is still connected
   * Also checks for disconnected participants
   * 
   * @param partnerId - Partner ID
   * @param workshopId - Workshop ID
   * @param userEmail - Email of participant sending heartbeat
   */
  async sendHeartbeat(
    partnerId: string,
    workshopId: string,
    userEmail: string
  ): Promise<void> {
    try {
      const now = Date.now();
      const workshopRef = doc(
        this.firestore,
        `partners/${partnerId}/workshops/${workshopId}`
      );
      
      // Sanitize email for Firestore field name (replace dots with underscores)
      const sanitizedEmail = this.sanitizeEmail(userEmail);
      
      // Update this participant's heartbeat
      await updateDoc(workshopRef, {
        [`participantPresence.${sanitizedEmail}.lastHeartbeat`]: now,
        [`participantPresence.${sanitizedEmail}.connectionStatus`]: 'online',
        updatedAt: now
      });
      
      
      // Check if any other participants have disconnected
      await this.checkForDisconnectedParticipants(partnerId, workshopId);
    } catch (error) {
      console.error('[WorkshopService] Error sending heartbeat:', error);
      // Don't throw - heartbeat failures shouldn't crash the app
    }
  }

  /**
   * Leave workshop explicitly (remove from connectedParticipants)
   * This is called when a user clicks "Leave" or navigates away
   * Does NOT update video currentTime - only updates participant connection status
   * 
   * @param partnerId - Partner ID
   * @param workshopId - Workshop ID
   * @param userEmail - Email of participant leaving
   */
  async leaveWorkshop(
    partnerId: string,
    workshopId: string,
    userEmail: string
  ): Promise<void> {
    try {
      const workshopRef = doc(
        this.firestore,
        `partners/${partnerId}/workshops/${workshopId}`
      );

      const now = Date.now();
      const sanitizedEmail = this.sanitizeEmail(userEmail);

      await runTransaction(this.firestore, async (transaction) => {
        const workshopDoc = await transaction.get(workshopRef);
        if (!workshopDoc.exists()) {
          console.error('[WorkshopService] Workshop not found when leaving');
          return;
        }

        const workshop = workshopDoc.data() as Workshop;
        const connected = workshop.connectedParticipants || [];
        const disconnected = workshop.disconnectedParticipants || [];

        const remainingConnected = connected.filter(e => e !== userEmail);
        const updatedDisconnected = connected.includes(userEmail) && !disconnected.includes(userEmail)
          ? [...disconnected, userEmail]
          : disconnected;

        const updates: Record<string, any> = {
          connectedParticipants: remainingConnected,
          disconnectedParticipants: updatedDisconnected,
          [`participantPresence.${sanitizedEmail}.connectionStatus`]: 'disconnected',
          [`participantPresence.${sanitizedEmail}.disconnectedAt`]: now,
          updatedAt: now,
        };

        // If the leaving user is the current host, hand off to the next in line
        // before they go so the remaining participants are never without a host.
        if (workshop.host === userEmail) {
          const nextHost = remainingConnected[0] ?? null;
          updates['host'] = nextHost;
        }

        transaction.update(workshopRef, updates);
      });

    } catch (error) {
      console.error('[WorkshopService] Error leaving workshop:', error);
      // Don't throw — we still want to navigate away even if the update fails
    }
  }

  /**
   * Atomically claim sync leadership by moving userEmail to connectedParticipants[0]
   * and writing the new playback state in a single Firestore transaction.
   *
   * Called whenever any participant presses Play or Pause so that whoever
   * touched the controls becomes the authoritative sync leader.  The previous
   * leader slides to [1] and becomes the watchdog; it will receive the snapshot,
   * see isSyncLeader()=false, and apply the remote playback command normally.
   */
  async claimSyncLeadership(
    partnerId: string,
    workshopId: string,
    userEmail: string,
    playbackState: {
      playingStatus: 'playing' | 'paused' | 'over';
      sessionStatus?: 'scheduled' | 'started' | 'completed';
      modules?: WorkshopModule[];
      leaderHeartbeatAt?: number;
    }
  ): Promise<void> {
    const workshopRef = doc(
      this.firestore,
      `partners/${partnerId}/workshops/${workshopId}`
    );

    await runTransaction(this.firestore, async (transaction) => {
      const workshopDoc = await transaction.get(workshopRef);
      if (!workshopDoc.exists()) {
        console.error('[WorkshopService] Workshop not found for leadership claim');
        return;
      }

      const workshop = workshopDoc.data() as Workshop;
      const current = workshop.connectedParticipants || [];

      // Move userEmail to position [0], preserving the relative order of others
      const others = current.filter(e => e !== userEmail);
      const reordered = [userEmail, ...others];

      const update: Record<string, any> = {
        connectedParticipants: reordered,
        playingStatus: playbackState.playingStatus,
        updatedAt: Date.now(),
      };
      if (playbackState.sessionStatus !== undefined) {
        update['sessionStatus'] = playbackState.sessionStatus;
      }
      if (playbackState.modules !== undefined) {
        update['modules'] = playbackState.modules;
      }
      if (playbackState.leaderHeartbeatAt !== undefined) {
        update['leaderHeartbeatAt'] = playbackState.leaderHeartbeatAt;
      }

      transaction.update(workshopRef, update);
    });

  }

  /**
   * Claim host for a participant.
   * Sets the workshop's host field to userEmail and moves them to
   * connectedParticipants[0] in a single atomic transaction.
   * Only the host can play/pause the workshop.
   */
  async claimHost(
    partnerId: string,
    workshopId: string,
    userEmail: string
  ): Promise<void> {
    const workshopRef = doc(
      this.firestore,
      `partners/${partnerId}/workshops/${workshopId}`
    );

    await runTransaction(this.firestore, async (transaction) => {
      const workshopDoc = await transaction.get(workshopRef);
      if (!workshopDoc.exists()) {
        console.error('[WorkshopService] Workshop not found for host claim');
        return;
      }

      const workshop = workshopDoc.data() as Workshop;
      const current = workshop.connectedParticipants || [];

      // Move userEmail to position [0], preserving relative order of others
      const others = current.filter(e => e !== userEmail);
      const reordered = [userEmail, ...others];

      transaction.update(workshopRef, {
        host: userEmail,
        connectedParticipants: reordered,
        updatedAt: Date.now(),
      });
    });

  }

  /**
   * Demote a stale sync leader detected by the watchdog (connectedParticipants[1]).
   * Atomically removes the email from connectedParticipants[0] and appends it to
   * the END of connectedParticipants so they remain "connected" but are no longer
   * the leader. The watchdog slides from [1] → [0] and its isSyncLeader computed
   * signal becomes true on the next Firestore snapshot.
   *
   * The demoted user is NOT added to disconnectedParticipants — they are still
   * actively connected; they were just a ghost leader (e.g. their tab froze).
   */
  async removeConnectedParticipant(
    partnerId: string,
    workshopId: string,
    userEmail: string
  ): Promise<void> {
    try {
      const workshopRef = doc(
        this.firestore,
        `partners/${partnerId}/workshops/${workshopId}`
      );

      await runTransaction(this.firestore, async (transaction) => {
        const workshopDoc = await transaction.get(workshopRef);
        if (!workshopDoc.exists()) return;

        const workshop = workshopDoc.data() as Workshop;
        // Remove from current position and re-append to the end
        const withoutGhost = (workshop.connectedParticipants || []).filter(e => e !== userEmail);
        const reordered = [...withoutGhost, userEmail];

        const update: Record<string, any> = {
          connectedParticipants: reordered,
          updatedAt: Date.now(),
        };

        // If the demoted user was the host, promote the new connectedParticipants[0]
        if (workshop.host === userEmail) {
          update['host'] = withoutGhost[0] ?? null;
        }

        transaction.update(workshopRef, update);
      });

    } catch (error) {
      console.error('[WorkshopService] Error demoting stale leader:', error);
    }
  }

  /**
   * Check all connected participants for missed heartbeats
   * Moves participants who haven't sent heartbeat in DISCONNECT_THRESHOLD to disconnectedParticipants
   * 
   * @param partnerId - Partner ID
   * @param workshopId - Workshop ID
   */
  async checkForDisconnectedParticipants(
    partnerId: string,
    workshopId: string
  ): Promise<void> {
    try {
      const workshop = await this.getWorkshopById(partnerId, workshopId);
      if (!workshop) {
        console.warn('[WorkshopService] Workshop not found for disconnect check');
        return;
      }
      
      const now = Date.now();
      const disconnected: string[] = [];
      
      // Check each connected participant
      for (const email of workshop.connectedParticipants) {
        const sanitizedEmail = this.sanitizeEmail(email);
        const presence = workshop.participantPresence?.[sanitizedEmail];
        
        if (!presence || !presence.lastHeartbeat) {
          // No heartbeat data yet - give them grace period (they just joined)
          continue;
        }
        
        const timeSinceLastBeat = now - presence.lastHeartbeat;
        
        if (timeSinceLastBeat > this.DISCONNECT_THRESHOLD) {
          disconnected.push(email);
        }
      }
      
      if (disconnected.length > 0) {
        await this.moveToDisconnected(partnerId, workshopId, disconnected);
      }
    } catch (error) {
      console.error('[WorkshopService] Error checking for disconnected participants:', error);
    }
  }

  /**
   * Move participants from connectedParticipants to disconnectedParticipants
   * Updates their presence status to 'disconnected'
   * 
   * @param partnerId - Partner ID
   * @param workshopId - Workshop ID
   * @param disconnectedEmails - Array of emails to mark as disconnected
   */
  private async moveToDisconnected(
    partnerId: string,
    workshopId: string,
    disconnectedEmails: string[]
  ): Promise<void> {
    try {
      const workshopRef = doc(
        this.firestore,
        `partners/${partnerId}/workshops/${workshopId}`
      );
      
      // Get current workshop state
      const workshop = await this.getWorkshopById(partnerId, workshopId);
      if (!workshop) return;
      
      // Filter out duplicates and update lists
      const currentDisconnected = workshop.disconnectedParticipants || [];
      const newDisconnected = [...new Set([...currentDisconnected, ...disconnectedEmails])];
      
      const stillConnected = workshop.connectedParticipants.filter(
        email => !disconnectedEmails.includes(email)
      );
      
      // Build presence updates
      const presenceUpdates: Record<string, unknown> = {};
      const now = Date.now();
      
      disconnectedEmails.forEach(email => {
        const sanitizedEmail = this.sanitizeEmail(email);
        presenceUpdates[`participantPresence.${sanitizedEmail}.connectionStatus`] = 'disconnected';
        presenceUpdates[`participantPresence.${sanitizedEmail}.disconnectedAt`] = now;
      });
      
      // Update workshop
      await updateDoc(workshopRef, {
        connectedParticipants: stillConnected,
        disconnectedParticipants: newDisconnected,
        ...presenceUpdates,
        updatedAt: now
      });
      
    } catch (error) {
      console.error('[WorkshopService] Error moving participants to disconnected:', error);
    }
  }

  /**
   * Mark a participant as reconnected (moves from disconnectedParticipants back to connectedParticipants)
   * This is automatically called when a previously disconnected participant sends a heartbeat
   * 
   * @param partnerId - Partner ID
   * @param workshopId - Workshop ID
   * @param userEmail - Email of participant who reconnected
   */
  async handleReconnection(
    partnerId: string,
    workshopId: string,
    userEmail: string
  ): Promise<void> {
    try {
      const workshop = await this.getWorkshopById(partnerId, workshopId);
      if (!workshop) return;
      
      // Check if user is in disconnected list
      if (workshop.disconnectedParticipants?.includes(userEmail)) {
        const workshopRef = doc(
          this.firestore,
          `partners/${partnerId}/workshops/${workshopId}`
        );
        
        const now = Date.now();
        const sanitizedEmail = this.sanitizeEmail(userEmail);
        
        // Remove from disconnected, add to connected
        const updatedDisconnected = workshop.disconnectedParticipants.filter(
          email => email !== userEmail
        );
        const updatedConnected = [...workshop.connectedParticipants, userEmail];
        
        await updateDoc(workshopRef, {
          connectedParticipants: updatedConnected,
          disconnectedParticipants: updatedDisconnected,
          [`participantPresence.${sanitizedEmail}.connectionStatus`]: 'online',
          [`participantPresence.${sanitizedEmail}.reconnectedAt`]: now,
          updatedAt: now
        });
        
      }
    } catch (error) {
      console.error('[WorkshopService] Error handling reconnection:', error);
    }
  }

  /**
   * Sanitize email for use as Firestore field name
   * Replaces dots with underscores since Firestore doesn't allow dots in field names
   */
  private sanitizeEmail(email: string): string {
    return email.replace(/\./g, '_');
  }

  /**
   * @deprecated Use submitAnswerV2 instead
   */
  async submitAnswer(
    workshopIdOrParams: string | { workshopId: string | undefined; userEmail: string; questionId: string; answer: string },
    userEmail?: string,
    questionId?: string,
    answer?: string
  ): Promise<void> {
    throw new Error('submitAnswer is deprecated. Use submitAnswerV2 instead.');
  }

  /**
   * Pause video at checkpoint (called by video timeupdate)
   */
  async pauseAtCheckpoint(workshopId: string, checkpointIndex: number): Promise<void> {
    const workshopRef = doc(this.firestore, 'workshops', workshopId);
    
    await updateDoc(workshopRef, {
      currentCheckpoint: checkpointIndex,
      playingStatus: 'paused'
    });
  }

  /**
   * Start the workshop (first play)
   */
  async startWorkshop(workshopId: string): Promise<void> {
    const workshopRef = doc(this.firestore, 'workshops', workshopId);
    
    await updateDoc(workshopRef, {
      sessionStatus: 'started',
      playingStatus: 'playing',
    });
  }

  /**
   * Check if all participants have answered a specific question
   */
  private checkAllParticipantsAnswered(
    participants: string[], 
    responses: { [userEmail: string]: { [questionId: string]: ActivityResponse } },
    questionId: string
  ): boolean {
    return participants.every(email => {
      return responses[email]?.[questionId] !== undefined;
    });
  }

  /**
   * Get current workshop value (not observable)
   */
  getCurrentWorkshop(): Workshop | null {
    return this.workshopSubject.value;
  }

  /**
   * Check if a user has answered/completed a specific activity
   * @param workshop - Workshop object
   * @param userEmail - User's email
   * @param activityId - Activity ID to check
   * @param moduleIndex - Index of the module containing the activity (defaults to currentModuleIndex)
   */
  hasUserAnsweredActivity(
    workshop: Workshop,
    userEmail: string,
    activityId: string,
    moduleIndex?: number
  ): boolean {
    const videoIndex = moduleIndex ?? workshop.currentModuleIndex;
    const currentVideo = workshop.modules?.[videoIndex];
    
    if (!currentVideo || !currentVideo.responses) {
      return false;
    }
    
    const userResponses = currentVideo.responses[userEmail];
    return !!(userResponses && userResponses[activityId]);
  }

  /**
   * Get list of participants who haven't completed a specific activity
   * @param workshop - Workshop object
   * @param activityId - Activity ID to check
   * @param moduleIndex - Index of the module containing the activity (defaults to currentModuleIndex)
   */
  getPendingParticipantsForActivity(
    workshop: Workshop,
    activityId: string,
    moduleIndex?: number
  ): string[] {
    const videoIndex = moduleIndex ?? workshop.currentModuleIndex;
    const currentVideo = workshop.modules?.[videoIndex];
    
    if (!currentVideo || !currentVideo.responses) {
      return workshop.invitedParticipants;
    }
    
    const pending: string[] = [];
    for (const email of workshop.invitedParticipants) {
      const userResponses = currentVideo.responses[email];
      if (!userResponses || !userResponses[activityId]) {
        pending.push(email);
      }
    }
    return pending;
  }

  /**
   * Get count of participants who haven't completed a specific activity
   * @param workshop - Workshop object
   * @param activityId - Activity ID to check
   * @param moduleIndex - Index of the module containing the activity (defaults to currentModuleIndex)
   */
  getPendingParticipantsCount(
    workshop: Workshop,
    activityId: string,
    moduleIndex?: number
  ): number {
    return this.getPendingParticipantsForActivity(workshop, activityId, moduleIndex).length;
  }

  /**
   * Subscribe to workshop (returns unsubscribe function)
   */
  subscribeToWorkshop(
    workshopId: string,
    callback: (workshop: Workshop) => void
  ): Unsubscribe {
    const workshopRef = doc(this.firestore, 'workshops', workshopId);
    
    return onSnapshot(workshopRef, (snapshot) => {
      if (snapshot.exists()) {
        const workshop = { id: snapshot.id, ...snapshot.data() } as Workshop;
        callback(workshop);
      }
    });
  }

  /**
   * Update current checkpoint index
   */
  async updateCurrentCheckpoint(workshopId: string | undefined, checkpointIndex: number): Promise<void> {
    if (!workshopId) return;
    
    const workshopRef = doc(this.firestore, 'workshops', workshopId);
    await updateDoc(workshopRef, {
      currentCheckpoint: checkpointIndex,
      playingStatus: 'paused'
    });
  }

  /**
   * Update workshop state with multiple fields
   */
  async updateWorkshopState(workshopId: string | undefined, updates: Partial<Workshop>): Promise<void> {
    if (!workshopId) return;
    
    const workshopRef = doc(this.firestore, 'workshops', workshopId);
    await updateDoc(workshopRef, updates);
  }

  /**
   * Check if all invited participants have completed a specific activity
   * @param workshop - Workshop object
   * @param activityId - Activity ID to check
   * @param moduleIndex - Index of the module containing the activity (defaults to currentModuleIndex)
   */
  haveAllParticipantsAnsweredActivity(
    workshop: Workshop,
    activityId: string,
    moduleIndex?: number
  ): boolean {
    const videoIndex = moduleIndex ?? workshop.currentModuleIndex;
    const currentVideo = workshop.modules?.[videoIndex];
    
    if (!currentVideo || !currentVideo.responses) {
      return false;
    }
    
    for (const email of workshop.invitedParticipants) {
      const userResponses = currentVideo.responses[email];
      if (!userResponses || !userResponses[activityId]) {
        return false;
      }
    }
    
    return true;
  }


  /**
   * Sync video position across all participants
   */
  async syncVideoPosition(workshopId: string, currentTime: number): Promise<void> {
    if (!workshopId) return;
    
    const workshopRef = doc(this.firestore, 'workshops', workshopId);
    await updateDoc(workshopRef, {
      currentVideoTime: currentTime
    });
  }

  // ============================================================================
  // Workshop Reference Management (Partner-Level Architecture)
  // ============================================================================

  /**
   * Create workshop reference documents for invited participants
   * Stored at partner level: partners/{partnerId}/workshop_refs/{refId}
   * RefId format: {workshopId}_{sanitizedUserEmail}
   */
  /**
   * Upsert user_academy_progress entries for a list of users and module IDs.
   * Rules:
   *  - Creates the document if it does not exist yet.
   *  - For each moduleId, only sets baseSet to the given status if the current value
   *    is NOT already 'Complete' (never downgrade a completed module).
   *  - Does not touch overrideSets or assessment fields.
   */
  private async upsertWatchedModulesStatus(
    partnerId: string,
    userEmails: string[],
    moduleIds: string[],
    status: WorkshopModuleStatus
  ): Promise<void> {
    if (!moduleIds.length || !userEmails.length) return;

    await Promise.all(userEmails.map(async (userEmail) => {
      try {
        // Top-level collection: user_academy_progress/{userEmail}
        const watchedRef = doc(this.firestore, `user_academy_progress/${userEmail}`);
        const snapshot = await getDoc(watchedRef);
        const existing: UserWatchedModules = snapshot.exists()
          ? (snapshot.data() as UserWatchedModules)
          : { partnerId, modules: {} };

        const updatedModules: Record<string, WatchedModuleEntry> = { ...existing.modules };

        for (const moduleId of moduleIds) {
          const current = updatedModules[moduleId];
          // Never downgrade a module that has already been completed
          if (current?.workshop?.baseSet === 'Complete') continue;

          updatedModules[moduleId] = {
            assessment: current?.assessment ?? false,
            workshop: {
              baseSet: status,
              overrideSets: current?.workshop?.overrideSets ?? {},
            },
          };
        }

        await setDoc(watchedRef, { partnerId, modules: updatedModules, updatedAt: Date.now() });
      } catch (error) {
        console.error(`[WorkshopService] Error upserting user_academy_progress for ${userEmail}:`, error);
      }
    }));
  }

  private async createWorkshopReferencesForParticipants(
    partnerId: string,
    workshopId: string,
    workshop: Omit<Workshop, 'id'>
  ): Promise<void> {
    const now = Date.now();
    
    
    // Create references for all invited participants in parallel
    const referencePromises = workshop.invitedParticipants.map(async (userEmail) => {
      const sanitizedEmail = userEmail.replace(/\./g, '_');
      const refId = `${workshopId}_${sanitizedEmail}`;
      const refPath = `partners/${partnerId}/workshop_refs/${refId}`;
      const refDoc = doc(this.firestore, refPath);
      
      const reference: WorkshopReference = {
        id: refId,
        workshopId: workshopId,
        partnerId: partnerId,
        userEmail: userEmail, // INDEXED field
        role: 'participant',
        status: 'invited',
        createdAt: now,
        
        // Cached display data - only essential fields for card display
        title: workshop.title,
        description: workshop.description,
        scheduledDate: workshop.scheduledDate,
        sessionStatus: workshop.sessionStatus,
        playingStatus: workshop.playingStatus,
        moduleCount: workshop.modules.length,
        
        hasUnreadActivity: false,
        notificationsEnabled: true,
      };
      
      
      await setDoc(refDoc, reference);
    });
    
    await Promise.all(referencePromises);

    // Cascade: mark all invited participants' user_academy_progress entries as 'Scheduled'
    const moduleIds = workshop.modules.map(m => m.moduleId);
    await this.upsertWatchedModulesStatus(partnerId, workshop.invitedParticipants, moduleIds, 'Scheduled');
  }

  /**
   * Create workshop references for specific new participants (used when editing workshop)
   * Stored at partner level with composite ID
   */
  private async createWorkshopReferencesForNewParticipants(
    partnerId: string,
    workshopId: string,
    workshop: Workshop,
    participantEmails: string[]
  ): Promise<void> {
    const now = Date.now();
    
    // Create references for the specified participants in parallel
    const referencePromises = participantEmails.map(async (userEmail) => {
      const sanitizedEmail = userEmail.replace(/\./g, '_');
      const refId = `${workshopId}_${sanitizedEmail}`;
      const refPath = `partners/${partnerId}/workshop_refs/${refId}`;
      const refDoc = doc(this.firestore, refPath);
      
      const reference: WorkshopReference = {
        id: refId,
        workshopId: workshopId,
        partnerId: partnerId,
        userEmail: userEmail, // INDEXED field
        role: 'participant',
        status: 'invited',
        createdAt: now,
        
        // Cached display data - only essential fields for card display
        title: workshop.title,
        description: workshop.description,
        scheduledDate: workshop.scheduledDate,
        sessionStatus: workshop.sessionStatus,
        playingStatus: workshop.playingStatus,
        moduleCount: workshop.modules.length,
        
        hasUnreadActivity: false,
        notificationsEnabled: true,
      };
      
      await setDoc(refDoc, reference);
    });
    
    await Promise.all(referencePromises);

    // Cascade: mark newly added participants' user_academy_progress entries as 'Scheduled'
    const moduleIds = workshop.modules.map(m => m.moduleId);
    await this.upsertWatchedModulesStatus(partnerId, participantEmails, moduleIds, 'Scheduled');
  }

  /**
   * Update cached data in all workshop references for a specific workshop
   * Called when workshop title, date, status, or other cached fields change
   */
  private async updateWorkshopReferencesCache(
    partnerId: string,
    workshopId: string,
    updates: Partial<Pick<WorkshopReference, 'title' | 'description' | 'scheduledDate' | 'sessionStatus' | 'playingStatus' | 'moduleCount'>>
  ): Promise<void> {
    try {
      
      // Query all refs for this workshop
      const refsCollection = collection(this.firestore, `partners/${partnerId}/workshop_refs`);
      const q = query(refsCollection, where('workshopId', '==', workshopId));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return;
      }
      
      // Batch update all refs
      const updatePromises = snapshot.docs.map(async (refDoc) => {
        await updateDoc(refDoc.ref, updates as any);
      });
      
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('[WorkshopService] Error updating workshop references cache:', error);
      throw error;
    }
  }

  /**
   * Remove workshop references for specific users (used when removing participants)
   */
  private async removeWorkshopReferencesForUsers(
    partnerId: string,
    workshopId: string,
    userEmails: string[],
    moduleIds: string[] = []
  ): Promise<void> {
    try {
      
      // Delete refs for each user
      const deletePromises = userEmails.map(async (userEmail) => {
        const sanitizedEmail = userEmail.replace(/\./g, '_');
        const refId = `${workshopId}_${sanitizedEmail}`;
        const refPath = `partners/${partnerId}/workshop_refs/${refId}`;
        const refDoc = doc(this.firestore, refPath);
        
        await deleteDoc(refDoc);
      });
      
      await Promise.all(deletePromises);

      // Cascade: reset removed participants' user_academy_progress entries to 'Not Started'
      if (moduleIds.length > 0) {
        await this.upsertWatchedModulesStatus(partnerId, userEmails, moduleIds, 'Not Started');
      }
    } catch (error) {
      console.error('[WorkshopService] Error removing workshop references:', error);
      throw error;
    }
  }

  /**
   * Delete all workshop references for a workshop (used when deleting workshop)
   */
  private async deleteAllWorkshopReferences(
    partnerId: string,
    workshopId: string
  ): Promise<void> {
    try {
      
      // Query all refs for this workshop
      const refsCollection = collection(this.firestore, `partners/${partnerId}/workshop_refs`);
      const q = query(refsCollection, where('workshopId', '==', workshopId));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return;
      }
      
      // Batch delete all refs
      const deletePromises = snapshot.docs.map(async (refDoc) => {
        await deleteDoc(refDoc.ref);
      });
      
      await Promise.all(deletePromises);
    } catch (error) {
      console.error('[WorkshopService] Error deleting workshop references:', error);
      throw error;
    }
  }

  /**
   * Delete workshop references for specific participants (used when removing participants)
   */
  private async deleteWorkshopReferencesForParticipants(
    partnerId: string,
    workshopId: string,
    participantEmails: string[]
  ): Promise<void> {
    // Delete references for the specified participants in parallel
    const deletePromises = participantEmails.map(async (userEmail) => {
      const refPath = `partners/${partnerId}/users/${userEmail}/workshop_refs/${workshopId}`;
      const refDoc = doc(this.firestore, refPath);
      
      try {
        await deleteDoc(refDoc);
      } catch (error) {
        console.error('[WorkshopService] Failed to delete reference for:', userEmail, error);
        // Continue with other deletions even if one fails
      }
    });
    
    await Promise.all(deletePromises);
  }

  /**
   * Update cached data in all participant workshop references
   * Call this after updating the main workshop document to keep refs in sync
   * 
   * @param partnerId - Partner ID
   * @param workshopId - Workshop ID
   * @param updates - Partial updates to apply to all workshop references (cached fields only)
   */
  private async updateWorkshopReferencesForAllParticipants(
    partnerId: string,
    workshopId: string,
    updates: Partial<WorkshopReference>
  ): Promise<void> {
    try {
      // First, get the workshop to find all participants
      const workshopRef = doc(this.firestore, `partners/${partnerId}/workshops/${workshopId}`);
      const workshopDoc = await getDoc(workshopRef);
      
      if (!workshopDoc.exists()) {
        console.warn('[WorkshopService] Workshop not found, cannot update references');
        return;
      }
      
      const workshop = workshopDoc.data() as Workshop;
      const participants = workshop.invitedParticipants || [];
      
      if (participants.length === 0) {
        return;
      }
      
      // Update all participant refs in parallel
      const updatePromises = participants.map(async (userEmail) => {
        const refPath = `partners/${partnerId}/users/${userEmail}/workshop_refs/${workshopId}`;
        const refDoc = doc(this.firestore, refPath);
        
        try {
          await updateDoc(refDoc, updates);
        } catch (error) {
          console.error('[WorkshopService] Failed to update reference for:', userEmail, error);
          // Continue with other updates even if one fails
        }
      });
      
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('[WorkshopService] Error updating workshop references:', error);
      // Don't throw - reference updates are not critical enough to fail the main update
    }
  }

  /**
   * Load user's workshop references (for displaying cards)
   * Returns lightweight reference documents, not full workshops
   */
  async loadUserWorkshopReferences(
    partnerId: string,
    userEmail: string
  ): Promise<WorkshopReference[]> {
    const refsCollection = collection(
      this.firestore,
      `partners/${partnerId}/users/${userEmail}/workshop_refs`
    );
    
    const q = query(refsCollection, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as WorkshopReference));
  }

  /**
   * Subscribe to user's workshop references (real-time updates)
   * This is what the sandbox page uses to display workshop cards
   * 
   * Queries partner-level workshop_refs collection filtered by userEmail
   */
  subscribeToUserWorkshopReferences(
    partnerId: string,
    userEmail: string,
    callback: (refs: WorkshopReference[]) => void
  ): Unsubscribe {
    const refsCollection = collection(
      this.firestore,
      `partners/${partnerId}/workshop_refs`
    );
    
    
    const q = query(
      refsCollection,
      where('userEmail', '==', userEmail),
      orderBy('createdAt', 'desc')
    );
    
    return onSnapshot(q, (snapshot) => {
      
      const refs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as WorkshopReference));
      
      callback(refs);
    }, (error) => {
      console.error('[WorkshopService] Error in workshop refs subscription:', error);
    });
  }

  /**
   * Update workshop reference when user joins
   * Called when user clicks "Join Workshop" button
   */
  async updateWorkshopReferenceOnJoin(
    partnerId: string,
    workshopId: string,
    userEmail: string
  ): Promise<void> {
    const sanitizedEmail = userEmail.replace(/\./g, '_');
    const refId = `${workshopId}_${sanitizedEmail}`;
    const refPath = `partners/${partnerId}/workshop_refs/${refId}`;
    const refDoc = doc(this.firestore, refPath);
    
    await updateDoc(refDoc, {
      status: 'joined',
      joinedAt: Date.now(),
      lastViewedAt: Date.now(),
    });
  }

  /**
   * Subscribe to partner-level workshop (for real-time collaboration)
   * This is what participants use when they join a workshop
   */
  subscribeToPartnerWorkshop(
    partnerId: string,
    workshopId: string,
    callback: (workshop: Workshop) => void
  ): Unsubscribe {
    const workshopRef = doc(
      this.firestore,
      `partners/${partnerId}/workshops/${workshopId}`
    );
    
    return onSnapshot(workshopRef, (snapshot) => {
      if (snapshot.exists()) {
        callback({ id: snapshot.id, ...snapshot.data() } as Workshop);
      }
    });
  }

  /**
   * Join workshop (partner-level)
   * Updates both the main workshop and the user's reference
   * Handles reconnection if user was previously disconnected
   */
  async joinPartnerWorkshop(
    partnerId: string,
    workshopId: string,
    userEmail: string
  ): Promise<void> {
    const workshopRef = doc(
      this.firestore,
      `partners/${partnerId}/workshops/${workshopId}`
    );

    const now = Date.now();
    const sanitizedEmail = this.sanitizeEmail(userEmail);
    let wasDisconnected = false;
    let workshopSessionStatus: string | undefined;

    // Use a transaction so that concurrent joins don't stomp each other's
    // connectedParticipants write or create duplicate host assignments.
    await runTransaction(this.firestore, async (transaction) => {
      const workshopDoc = await transaction.get(workshopRef);
      if (!workshopDoc.exists()) {
        throw new Error('Workshop not found');
      }

      const workshopData = workshopDoc.data() as Workshop;
      workshopSessionStatus = workshopData.sessionStatus;
      wasDisconnected = !!workshopData.disconnectedParticipants?.includes(userEmail);

      const connected = workshopData.connectedParticipants || [];
      const disconnected = workshopData.disconnectedParticipants || [];
      const updates: Record<string, any> = {};

      if (wasDisconnected) {
        // Move from disconnected → connected (filter then append)
        updates['disconnectedParticipants'] = disconnected.filter(e => e !== userEmail);
        updates['connectedParticipants'] = [...connected.filter(e => e !== userEmail), userEmail];
        updates[`participantPresence.${sanitizedEmail}.reconnectedAt`] = now;
      } else if (!connected.includes(userEmail)) {
        // First-time join — append to the end of the array atomically
        updates['connectedParticipants'] = [...connected, userEmail];
      }

      // Resolve the final connectedParticipants array BEFORE deciding on host,
      // so the check reflects what will actually be committed rather than the
      // stale Firestore state at read time.
      const finalConnected: string[] = updates['connectedParticipants'] ?? connected;

      // Assign (or re-assign) host only when:
      //   (a) no host is set yet, OR
      //   (b) the declared host is no longer at position [0] of the final array
      //       (i.e. they left or were never in the array — stale host field).
      // Always promote whoever will end up at [0], not necessarily this joining user.
      if (!workshopData.host || finalConnected[0] !== workshopData.host) {
        updates['host'] = finalConnected[0] ?? userEmail;
      }

      // Presence heartbeat
      updates[`participantPresence.${sanitizedEmail}.lastHeartbeat`] = now;
      updates[`participantPresence.${sanitizedEmail}.connectionStatus`] = 'online';

      // Always stamp the join timestamp so the host can detect a page-reload
      // reconnect even when the user was never removed from connectedParticipants.
      updates[`participantJoinedAt.${userEmail}`] = now;

      transaction.update(workshopRef, updates);
    });

    // Update the user's workshop_refs document outside the transaction
    // (no risk of conflict — each user's ref doc is unique to them)
    await this.updateWorkshopReferenceOnJoin(partnerId, workshopId, userEmail);

    // If reconnecting to an in-progress workshop, check for missed activities
    if (wasDisconnected && workshopSessionStatus === 'started') {
      this.runMissedActivityGapAnalysis(partnerId, workshopId, userEmail)
        .then(missedCount => {
          if (missedCount > 0) {
          }
        })
        .catch(err => {
          console.error('[WorkshopService] Error running reconnect gap analysis:', err);
        });
    }
  }

  /**
   * Update workshop state (partner-level)
   */
  async updatePartnerWorkshopState(
    partnerId: string,
    workshopId: string,
    updates: Partial<Workshop>
  ): Promise<void> {
    const workshopRef = doc(
      this.firestore,
      `partners/${partnerId}/workshops/${workshopId}`
    );
    await updateDoc(workshopRef, updates as any);
  }

  /**
   * Check if all connected participants have answered a question (partner-level)
   */
  checkAllConnectedParticipantsAnswered(
    workshop: Workshop,
    videoId: string,
    questionId: string
  ): boolean {
    const currentVideo = workshop.modules.find(v => v.id === videoId);
    if (!currentVideo) return false;
    
    return workshop.connectedParticipants.every(email => {
      return currentVideo.responses[email]?.[questionId] !== undefined;
    });
  }

  /**
   * Get count of pending connected participants for a question (partner-level)
   */
  getPendingConnectedParticipantsCount(
    workshop: Workshop,
    videoId: string,
    questionId: string
  ): number {
    const currentVideo = workshop.modules.find(v => v.id === videoId);
    if (!currentVideo) return 0;
    
    return workshop.connectedParticipants.filter(email =>
      !currentVideo.responses[email]?.[questionId]
    ).length;
  }

  /**
   * Submit answer to workshop question (partner-level)
   * @deprecated Use submitActivityResponse instead for step-based activities
   */
  async submitAnswerV2(
    partnerId: string,
    workshopId: string,
    userEmail: string,
    videoId: string,
    questionId: string,
    answer: string
  ): Promise<void> {
    throw new Error('submitAnswerV2 is deprecated. Use submitActivityResponse instead for step-based activities.');
  }

  /**
   * Get a workshop by ID (partner-level)
   * @param partnerId - Partner ID
   * @param workshopId - Workshop ID
   * @returns Workshop data or null if not found
   */
  async getWorkshopById(partnerId: string, workshopId: string): Promise<Workshop | null> {
    try {
      const workshopRef = doc(
        this.firestore,
        `partners/${partnerId}/workshops/${workshopId}`
      );
      const workshopDoc = await getDoc(workshopRef);
      
      if (!workshopDoc.exists()) {
        return null;
      }
      
      return {
        id: workshopDoc.id,
        ...workshopDoc.data()
      } as Workshop;
    } catch (error) {
      console.error('[WorkshopService] Error fetching workshop:', error);
      throw error;
    }
  }

  /**
   * Update ONLY the real-time playback state (playingStatus / sessionStatus).
   *
   * This is a FAST path: it issues a direct `updateDoc` with no prior `getDoc` read,
   * so the Firestore write (and the resulting `onSnapshot` for all listeners) happens
   * in ~100-300 ms instead of 1-2 s.  Use this for every play / pause / activity-pause
   * event where low latency is critical.
   *
   * Optionally pass the caller's LOCAL modules array (with an updated currentTime)
   * so the playback-state change and the seek position arrive in a single atomic
   * Firestore write — this eliminates the "stale currentTime" race that caused
   * followers to seek to the wrong position or miss activity triggers.
   */
  async updatePlaybackState(
    partnerId: string,
    workshopId: string,
    state: {
      playingStatus: 'playing' | 'paused' | 'over';
      sessionStatus?: 'scheduled' | 'started' | 'completed';
      /** Pass the FULL modules array (already patched with currentTime) to
       *  include it in the same write.  No getDoc needed — use the caller's
       *  local copy from workshop(). */
      modules?: WorkshopModule[];
      /** Written by the sync leader on every position-sync tick so the watchdog
       *  (connectedParticipants[1]) can detect a stale leader. */
      leaderHeartbeatAt?: number;
      /** Set when a participant reconnects mid-session; cleared when host plays. */
      pendingReconnect?: { email: string; at: number } | null;
      /** Phase 5: group session phase for activity UI reconciliation. */
      sessionPhase?: 'video' | 'activity';
      activeActivity?: WorkshopActiveActivityRef | null;
      lastCompletedActivity?: WorkshopLastCompletedActivityRef | null;
      /** First host play — set once with sessionStatus started. */
      startedAt?: number;
    }
  ): Promise<void> {
    const workshopRef = doc(
      this.firestore,
      `partners/${partnerId}/workshops/${workshopId}`
    );
    const update: Record<string, any> = {
      playingStatus: state.playingStatus,
      updatedAt: Date.now(),
    };
    if (state.sessionStatus !== undefined) {
      update['sessionStatus'] = state.sessionStatus;
    }
    if (state.startedAt !== undefined) {
      update['startedAt'] = state.startedAt;
    }
    if (state.modules !== undefined) {
      update['modules'] = state.modules;
    }
    if (state.leaderHeartbeatAt !== undefined) {
      update['leaderHeartbeatAt'] = state.leaderHeartbeatAt;
    }
    if (state.pendingReconnect !== undefined) {
      update['pendingReconnect'] = state.pendingReconnect ?? deleteField();
    }
    if (state.sessionPhase !== undefined) {
      update['sessionPhase'] = state.sessionPhase;
    }
    if (state.activeActivity !== undefined) {
      update['activeActivity'] =
        state.activeActivity === null ? deleteField() : state.activeActivity;
    }
    if (state.lastCompletedActivity !== undefined) {
      update['lastCompletedActivity'] = state.lastCompletedActivity;
    }
    await updateDoc(workshopRef, update);
  }

  /**
   * Update a workshop (partner-level)
   * @param partnerId - Partner ID
   * @param workshopId - Workshop ID
   * @param updates - Partial workshop data to update
   */
  async updateWorkshop(
    partnerId: string,
    workshopId: string,
    updates: Partial<Workshop> | Record<string, any>,
    options?: { isAdminEdit?: boolean }
  ): Promise<void> {
    try {
      
      const workshopRef = doc(
        this.firestore,
        `partners/${partnerId}/workshops/${workshopId}`
      );
      
      // Get current workshop data to detect new participants
      const workshopDoc = await getDoc(workshopRef);
      if (!workshopDoc.exists()) {
        throw new Error('Workshop not found');
      }
      
      const currentWorkshop = workshopDoc.data() as Workshop;
      
      // Only block admin form edits (title, participants, schedule) on started/completed workshops
      // Runtime updates (playingStatus, modules, sessionStatus, etc.) must always be allowed
      if (options?.isAdminEdit && currentWorkshop.sessionStatus !== 'scheduled') {
        throw new Error(`Cannot edit ${currentWorkshop.sessionStatus} workshop. Only scheduled workshops can be edited.`);
      }
      const oldParticipants = currentWorkshop.invitedParticipants || [];
      const newParticipants = (updates.invitedParticipants as string[]) || oldParticipants;
      
      // Find newly added participants
      const addedParticipants = newParticipants.filter(email => !oldParticipants.includes(email));
      
      // Find removed participants
      const removedParticipants = oldParticipants.filter(email => !newParticipants.includes(email));
      
      if (addedParticipants.length > 0) {
      }
      
      if (removedParticipants.length > 0) {
      }
      
      // Update the workshop document
      await updateDoc(workshopRef, {
        ...updates,
        updatedAt: Date.now(),
      });
      

      // Create WorkshopReference objects for newly added participants
      if (addedParticipants.length > 0) {
        const updatedWorkshop = { ...currentWorkshop, ...updates, id: workshopId };
        await this.createWorkshopReferencesForNewParticipants(
          partnerId,
          workshopId,
          updatedWorkshop as Workshop,
          addedParticipants
        );
      }

      // Delete WorkshopReference objects for removed participants
      if (removedParticipants.length > 0) {
        // Use the updated modules list if provided, otherwise fall back to the existing workshop's modules
        const effectiveModules = (updates.modules as Workshop['modules']) ?? currentWorkshop.modules;
        const moduleIds = effectiveModules.map(m => m.moduleId);
        await this.removeWorkshopReferencesForUsers(
          partnerId,
          workshopId,
          removedParticipants,
          moduleIds
        );
      }

      // Update cached data in all participant workshop references
      const refUpdates: Record<string, unknown> = {};
      if (updates.title !== undefined) refUpdates['title'] = updates.title;
      if (updates.description !== undefined) refUpdates['description'] = updates.description;
      if (updates.scheduledDate !== undefined) refUpdates['scheduledDate'] = updates.scheduledDate;
      if (updates.sessionStatus !== undefined) refUpdates['sessionStatus'] = updates.sessionStatus;
      if (updates.playingStatus !== undefined) refUpdates['playingStatus'] = updates.playingStatus;
      if (updates.modules !== undefined) refUpdates['moduleCount'] = (updates.modules as WorkshopModule[]).length;

      if (Object.keys(refUpdates).length > 0) {
        await this.updateWorkshopReferencesCache(partnerId, workshopId, refUpdates);
      }
    } catch (error) {
      console.error('[WorkshopService] Error updating workshop:', error);
      throw error;
    }
  }

  /**
   * Update current time for a specific video in the workshop
   * Firestore doesn't support nested array updates with dot notation,
   * so we need to fetch, update, and write back the entire modules array
   */
  async updateVideoCurrentTime(
    partnerId: string,
    workshopId: string,
    videoIndex: number,
    currentTime: number
  ): Promise<void> {
    try {
      const workshopRef = doc(
        this.firestore,
        `partners/${partnerId}/workshops/${workshopId}`
      );
      
      // Fetch current workshop data
      const workshopDoc = await getDoc(workshopRef);
      if (!workshopDoc.exists()) {
        console.error('[WorkshopService] Workshop not found for currentTime update');
        return;
      }
      
      const workshop = workshopDoc.data() as Workshop;
      if (!workshop.modules || !workshop.modules[videoIndex]) {
        console.error('[WorkshopService] Video not found at index:', videoIndex);
        return;
      }
      
      // Update the specific video's currentTime
      const updatedVideos = [...workshop.modules];
      updatedVideos[videoIndex] = {
        ...updatedVideos[videoIndex],
        currentTime: currentTime,
      };
      
      // Write back the entire modules array
      await updateDoc(workshopRef, {
        modules: updatedVideos, // ✅ Fixed: was "videos", should be "modules"
        updatedAt: Date.now(),
      });
    } catch (error) {
      console.error('[WorkshopService] Error updating video current time:', error);
    }
  }

  /**
   * Submit activity response with step-based answers
   * @param partnerId - Partner ID
   * @param workshopId - Workshop ID
   * @param userEmail - User's email
   * @param videoIndex - Index of the video in the workshop
   * @param activityResponse - Complete activity response with all step answers
   */
  async submitActivityResponse(
    partnerId: string,
    workshopId: string,
    userEmail: string,
    videoIndex: number,
    activityResponse: import('src/app/core/schema/Workshop.model').ActivityResponse
  ): Promise<void> {
    try {
      const workshopRef = doc(
        this.firestore,
        `partners/${partnerId}/workshops/${workshopId}`
      );

      // ── Atomic write using a Firestore transaction ───────────────────────────
      // A transaction provides optimistic-locking: if the document is modified
      // between the read and the write the transaction retries automatically.
      // This prevents the race condition where two users submitting at the same
      // time would each read the same empty state and overwrite each other's
      // response when they wrote back the full modules array.
      //
      // NOTE: Firestore does NOT support dot-notation path updates on array
      // elements (it would silently convert the array to a map).  Transactions
      // are the correct approach for array elements that must be updated safely.
      await runTransaction(this.firestore, async (transaction) => {
        const workshopDoc = await transaction.get(workshopRef);
        if (!workshopDoc.exists()) {
          throw new Error('Workshop not found');
        }

        const workshop = workshopDoc.data() as Workshop;
        if (!workshop.modules || !workshop.modules[videoIndex]) {
          throw new Error('Video not found at index: ' + videoIndex);
        }

        const updatedModules = [...workshop.modules];
        const video = { ...updatedModules[videoIndex] };

        if (!video.responses) video.responses = {};
        video.responses = {
          ...video.responses,
          [userEmail]: {
            ...(video.responses[userEmail] || {}),
            [activityResponse.activityId]: activityResponse,
          },
        };

        updatedModules[videoIndex] = video;

        // Phase 4: missedActivities for incomplete responses in the SAME transaction as
        // the response write (timer_expired, skipped, etc.) — no second round-trip.
        // Discussion-only activities are never tracked as missed (notes optional).
        const activityDef = video.activities?.find(a => a.id === activityResponse.activityId);
        const isAllDiscussion =
          !!activityDef?.steps?.length &&
          activityDef.steps.every(s => s.type === 'group-discussion');

        const payload: Record<string, unknown> = {
          modules: updatedModules,
          updatedAt: Date.now(),
        };

        if (activityResponse.isIncomplete && !isAllDiscussion) {
          const moduleId = video.moduleId;
          const sanitizedEmail = this.sanitizeEmail(userEmail);
          const existingIds: string[] =
            workshop.missedActivities?.[sanitizedEmail]?.[moduleId] ?? [];
          const aid = activityResponse.activityId;
          const updatedMissed = existingIds.includes(aid) ? existingIds : [...existingIds, aid];
          payload[`missedActivities.${sanitizedEmail}.${moduleId}`] = updatedMissed;
        }

        transaction.update(workshopRef, payload as any);
      });

    } catch (error) {
      console.error('[WorkshopService] Error submitting activity response:', error);
      throw error;
    }
  }

  /**
   * Single transaction: merge activity response + mark activity done.
   * Used after the live session listener is torn down (post-completion makeup) so
   * concurrent users catching up on missed activities do not interleave two writes.
   */
  async submitActivityResponseAndMarkActivityDone(
    partnerId: string,
    workshopId: string,
    userEmail: string,
    videoIndex: number,
    activityIndex: number,
    activityResponse: import('src/app/core/schema/Workshop.model').ActivityResponse
  ): Promise<void> {
    const workshopRef = doc(
      this.firestore,
      `partners/${partnerId}/workshops/${workshopId}`
    );

    await runTransaction(this.firestore, async (transaction) => {
      const workshopDoc = await transaction.get(workshopRef);
      if (!workshopDoc.exists()) {
        throw new Error('Workshop not found');
      }

      const workshop = workshopDoc.data() as Workshop;
      if (!workshop.modules || !workshop.modules[videoIndex]) {
        throw new Error('Video not found at index: ' + videoIndex);
      }

      const updatedModules = [...workshop.modules];
      const video = { ...updatedModules[videoIndex] };

      if (!video.responses) video.responses = {};
      video.responses = {
        ...video.responses,
        [userEmail]: {
          ...(video.responses[userEmail] || {}),
          [activityResponse.activityId]: activityResponse,
        },
      };

      if (
        !Array.isArray(video.activities) ||
        !video.activities[activityIndex]
      ) {
        throw new Error('Activity not found at index: ' + activityIndex);
      }

      const updatedActivities = video.activities.map((activity, idx) => {
        if (idx === activityIndex) {
          return {
            ...activity,
            done: true,
            steps: activity.steps.map(step => ({ ...step, done: true })),
          };
        }
        return activity;
      });

      updatedModules[videoIndex] = {
        ...video,
        activities: updatedActivities,
      };

      const activityDef = updatedActivities[activityIndex];
      const isAllDiscussion =
        !!activityDef?.steps?.length &&
        activityDef.steps.every(s => s.type === 'group-discussion');

      const payload: Record<string, unknown> = {
        modules: updatedModules,
        updatedAt: Date.now(),
      };

      if (activityResponse.isIncomplete && !isAllDiscussion) {
        const moduleId = video.moduleId;
        const sanitizedEmail = this.sanitizeEmail(userEmail);
        const existingIds: string[] =
          workshop.missedActivities?.[sanitizedEmail]?.[moduleId] ?? [];
        const aid = activityResponse.activityId;
        const updatedMissed = existingIds.includes(aid) ? existingIds : [...existingIds, aid];
        payload[`missedActivities.${sanitizedEmail}.${moduleId}`] = updatedMissed;
      }

      transaction.update(workshopRef, payload as any);
    });

  }

  /**
   * Mark an activity as done for a specific video
   */
  async markActivityDone(
    partnerId: string,
    workshopId: string,
    videoIndex: number,
    activityIndex: number,
    options?: { clearSessionActivityPointers?: boolean }
  ): Promise<void> {
    try {
      const workshopRef = doc(
        this.firestore,
        `partners/${partnerId}/workshops/${workshopId}`
      );

      // Use a transaction so concurrent markActivityDone calls don't overwrite each other
      await runTransaction(this.firestore, async (transaction) => {
        const workshopDoc = await transaction.get(workshopRef);
        if (!workshopDoc.exists()) {
          console.error('[WorkshopService] Workshop not found for activity update');
          return;
        }

        const workshop = workshopDoc.data() as Workshop;

        // Guard: modules must be a real array (could be corrupted)
        if (!Array.isArray(workshop.modules) || !workshop.modules[videoIndex]) {
          console.error('[WorkshopService] Video not found at index:', videoIndex);
          return;
        }

        if (!Array.isArray(workshop.modules[videoIndex].activities) ||
            !workshop.modules[videoIndex].activities[activityIndex]) {
          console.error('[WorkshopService] Activity not found at index:', activityIndex);
          return;
        }

        // Update the specific activity's done flag AND mark all steps as done
        const updatedModules = [...workshop.modules];
        updatedModules[videoIndex] = {
          ...updatedModules[videoIndex],
          activities: updatedModules[videoIndex].activities.map((activity, idx) => {
            if (idx === activityIndex) {
              return {
                ...activity,
                done: true,
                steps: activity.steps.map(step => ({ ...step, done: true })),
              };
            }
            return activity;
          }),
        };

        // Do NOT write playingStatus here — markActivityDone only manages the
        // modules array. playingStatus is owned by updatePlaybackState and the
        // play/pause control methods. Writing 'paused' here causes a race where
        // markActivityDone overwrites a 'playing' state that checkIfAllParticipantsDone
        // had just broadcast after seeing all responses, reverting the doc to 'paused'
        // and leaving followers stuck in the "Waiting for other participants" state.
        const activityIdForPointer =
          updatedModules[videoIndex].activities[activityIndex].id;
        const payload: Record<string, unknown> = {
          modules: updatedModules,
          updatedAt: Date.now(),
        };
        if (options?.clearSessionActivityPointers) {
          payload['sessionPhase'] = 'video';
          payload['activeActivity'] = deleteField();
          payload['lastCompletedActivity'] = {
            moduleIndex: videoIndex,
            activityId: activityIdForPointer,
          };
        }
        transaction.update(workshopRef, payload as any);
      });

    } catch (error) {
      console.error('[WorkshopService] Error marking activity as done:', error);
      throw error;
    }
  }

  /**
   * Delete a workshop and all associated references (partner-level)
   * @param partnerId - Partner ID
   * @param workshopId - Workshop ID
   */
  async deleteWorkshop(partnerId: string, workshopId: string): Promise<void> {
    try {
      // Get workshop to find invited participants
      const workshop = await this.getWorkshopById(partnerId, workshopId);
      if (!workshop) {
        throw new Error('Workshop not found');
      }

      // Prevent deleting workshops that have started or completed
      if (workshop.sessionStatus !== 'scheduled') {
        throw new Error(`Cannot delete ${workshop.sessionStatus} workshop. Only scheduled workshops can be deleted.`);
      }


      // 1. Delete all workshop references (cascade delete)
      await this.deleteAllWorkshopReferences(partnerId, workshopId);

      // 2. Delete the main workshop document
      const workshopRef = doc(
        this.firestore,
        `partners/${partnerId}/workshops/${workshopId}`
      );
      await deleteDoc(workshopRef);
      
    } catch (error) {
      console.error('[WorkshopService] ❌ Error deleting workshop:', error);
      throw error;
    }
  }

  /**
   * Register user presence for an activity
   * Called when a participant sees the activity popup on their screen.
   * Only users in this array are waited on for responses.
   * 
   * @param partnerId - Partner ID
   * @param workshopId - Workshop ID
   * @param moduleIndex - Module index
   * @param activityIndex - Activity index within the module
   * @param userEmail - Email of participant who is present
   */
  async registerActivityPresence(
    partnerId: string,
    workshopId: string,
    moduleIndex: number,
    activityIndex: number,
    userEmail: string
  ): Promise<void> {
    try {
      const workshopRef = doc(
        this.firestore,
        `partners/${partnerId}/workshops/${workshopId}`
      );

      // Use a transaction so that concurrent calls from leader and follower
      // (which can arrive within milliseconds of each other) don't stomp each
      // other's writes on the modules array.
      await runTransaction(this.firestore, async (transaction) => {
        const workshopDoc = await transaction.get(workshopRef);
        if (!workshopDoc.exists()) {
          console.error('[WorkshopService] Workshop not found for presence registration');
          return;
        }

        const workshop = workshopDoc.data() as Workshop;

        if (!Array.isArray(workshop.modules) || !workshop.modules[moduleIndex]) {
          console.error('[WorkshopService] Module not found at index:', moduleIndex);
          return;
        }

        const module = workshop.modules[moduleIndex];
        if (!Array.isArray(module.activities) || !module.activities[activityIndex]) {
          console.error('[WorkshopService] Activity not found at index:', activityIndex);
          return;
        }

        const activity = module.activities[activityIndex];
        const presentUsers: string[] = activity.presentUsers || [];

        if (presentUsers.includes(userEmail)) {
          return; // Already registered — no write needed
        }

        const updatedPresentUsers = [...presentUsers, userEmail];

        const updatedModules = [...workshop.modules];
        updatedModules[moduleIndex] = {
          ...updatedModules[moduleIndex],
          activities: updatedModules[moduleIndex].activities.map((a, i) =>
            i === activityIndex ? { ...a, presentUsers: updatedPresentUsers } : a
          ),
        };

        transaction.update(workshopRef, {
          modules: updatedModules,
          updatedAt: Date.now(),
        });

      });
    } catch (error) {
      console.error('[WorkshopService] Error registering activity presence:', error);
      // Don't throw — presence registration should not block the activity
    }
  }

  /**
   * Track missed activities for users who were NOT present when an activity was shown
   * Called after the grace period (a few seconds after activity is displayed).
   * Only the facilitator / first user to detect absence calls this.
   * 
   * @param partnerId - Partner ID
   * @param workshopId - Workshop ID
   * @param moduleIndex - Module index
   * @param activityIndex - Activity index
   */
  async trackAbsentUsersForActivity(
    partnerId: string,
    workshopId: string,
    moduleIndex: number,
    activityIndex: number
  ): Promise<void> {
    try {
      const workshop = await this.getWorkshopById(partnerId, workshopId);
      if (!workshop) return;

      const module = workshop.modules[moduleIndex];
      if (!module) return;

      const activity = module.activities[activityIndex];
      if (!activity) return;

      // Phase 3: activity already finalized — don't create false "absent" rows or placeholders
      if (activity.done) {
        return;
      }

      const presentUsers = activity.presentUsers || [];
      const connectedParticipants = workshop.connectedParticipants || [];
      const responses = module.responses || {};

      // Phase 3: everyone connected already has a response (fast submit race) — nothing to track
      if (
        connectedParticipants.length > 0 &&
        connectedParticipants.every(email => !!responses[email]?.[activity.id])
      ) {
        return;
      }

      // Find connected users who are NOT present for this activity
      const absentUsers = connectedParticipants.filter(
        email => !presentUsers.includes(email)
      );

      if (absentUsers.length === 0) {
        return;
      }


      const workshopRef = doc(
        this.firestore,
        `partners/${partnerId}/workshops/${workshopId}`
      );

      // Discussion-only activities are never "missed" — notes are optional.
      // Absent users still get a complete placeholder so the "all done" check passes.
      const isAllDiscussion = activity.steps?.length > 0 &&
        activity.steps.every(s => s.type === 'group-discussion');

      const updates: Record<string, unknown> = { updatedAt: Date.now() };

      for (const absentEmail of absentUsers) {
        const sanitizedEmail = this.sanitizeEmail(absentEmail);
        const userMissed = workshop.missedActivities?.[sanitizedEmail] || {};
        const moduleActivities = userMissed[module.moduleId] || [];

        // Only add to missedActivities for non-discussion activities
        if (!isAllDiscussion && !moduleActivities.includes(activity.id)) {
          updates[`missedActivities.${sanitizedEmail}.${module.moduleId}`] = [
            ...moduleActivities,
            activity.id
          ];
        }

        // Create a placeholder response so the workshop "all done" check can pass.
        // For discussion-only activities the placeholder is marked complete.
        const updatedModules = [...workshop.modules];
        const mod = updatedModules[moduleIndex];
        if (!mod.responses) mod.responses = {};
        if (!mod.responses[absentEmail]) mod.responses[absentEmail] = {};

        if (!mod.responses[absentEmail][activity.id]) {
          mod.responses[absentEmail][activity.id] = {
            activityId: activity.id,
            completedAt: Date.now(),
            completionTime: 0,
            steps: activity.steps.map(step => ({
              stepId: step.id,
              prompt: step.prompt,
              answer: '',
              answeredAt: Date.now(),
              isIncomplete: isAllDiscussion ? false : true,
            })),
            isIncomplete: isAllDiscussion ? false : true,
            ...(isAllDiscussion ? {} : { incompletionReason: 'disconnected' }),
          };
          updates['modules'] = updatedModules;
        }
      }

      await updateDoc(workshopRef, updates);
    } catch (error) {
      console.error('[WorkshopService] Error tracking absent users:', error);
    }
  }

  /**
   * Late join / rejoin while the group is on a live activity (`sessionPhase === 'activity'`).
   * Avoids parachuting into the live activity UI: writes a placeholder response plus
   * `missedActivities` (non–discussion-only) so the group can complete the round; this
   * user finishes in makeup later. Idempotent if a response already exists.
   *
   * @returns true when this client was registered as late for the current live activity.
   */
  async recordLateJoinSkipLiveActivity(
    partnerId: string,
    workshopId: string,
    userEmail: string
  ): Promise<boolean> {
    const workshopRef = doc(
      this.firestore,
      `partners/${partnerId}/workshops/${workshopId}`
    );
    const sanitizedEmail = this.sanitizeEmail(userEmail);
    let applied = false;

    try {
      await runTransaction(this.firestore, async (transaction) => {
        const workshopDoc = await transaction.get(workshopRef);
        if (!workshopDoc.exists()) {
          return;
        }

        const workshop = workshopDoc.data() as Workshop;
        if (workshop.sessionStatus !== 'started') {
          return;
        }
        if (workshop.sessionPhase !== 'activity' || !workshop.activeActivity) {
          return;
        }

        const { moduleIndex, activityId, activityIndex } = workshop.activeActivity;
        if (!Array.isArray(workshop.modules) || !workshop.modules[moduleIndex]) {
          return;
        }
        if (moduleIndex !== workshop.currentModuleIndex) {
          return;
        }

        const mod = workshop.modules[moduleIndex];
        const activity = mod.activities?.[activityIndex];
        if (!activity || activity.id !== activityId || activity.done) {
          return;
        }

        if (mod.responses?.[userEmail]?.[activityId]) {
          return;
        }

        const isAllDiscussion =
          activity.steps?.length > 0 &&
          activity.steps.every(s => s.type === 'group-discussion');

        const updatedModules = [...workshop.modules];
        const modCopy = { ...updatedModules[moduleIndex] };
        if (!modCopy.responses) {
          modCopy.responses = {};
        }
        modCopy.responses = {
          ...modCopy.responses,
          [userEmail]: {
            ...(modCopy.responses[userEmail] || {}),
            [activityId]: {
              activityId,
              completedAt: Date.now(),
              completionTime: 0,
              steps: activity.steps.map(step => ({
                stepId: step.id,
                prompt: step.prompt,
                answer: '',
                answeredAt: Date.now(),
                isIncomplete: isAllDiscussion ? false : true,
              })),
              isIncomplete: isAllDiscussion ? false : true,
              ...(isAllDiscussion ? {} : { incompletionReason: 'disconnected' as const }),
            },
          },
        };
        updatedModules[moduleIndex] = modCopy;

        const payload: Record<string, unknown> = {
          modules: updatedModules,
          updatedAt: Date.now(),
        };

        if (!isAllDiscussion) {
          const existingIds: string[] =
            workshop.missedActivities?.[sanitizedEmail]?.[mod.moduleId] ?? [];
          if (!existingIds.includes(activityId)) {
            payload[`missedActivities.${sanitizedEmail}.${mod.moduleId}`] = [
              ...existingIds,
              activityId,
            ];
          }
        }

        transaction.update(workshopRef, payload as any);
        applied = true;
      });

      if (applied) {
      }
      return applied;
    } catch (error) {
      console.error('[WorkshopService] recordLateJoinSkipLiveActivity failed:', error);
      return false;
    }
  }

  /**
   * Run gap analysis for a user — find any activities they missed responses for
   * Compares user's responses against all done activities across all modules.
   * Used on reconnect and at workshop completion.
   * 
   * @param partnerId - Partner ID
   * @param workshopId - Workshop ID
   * @param userEmail - User's email
   * @returns Number of newly tracked missed activities
   */
  async runMissedActivityGapAnalysis(
    partnerId: string,
    workshopId: string,
    userEmail: string
  ): Promise<number> {
    try {
      const workshop = await this.getWorkshopById(partnerId, workshopId);
      if (!workshop) return 0;

      // Guard: modules must be a proper array (could be missing or corrupted)
      if (!Array.isArray(workshop.modules)) {
        console.error('[WorkshopService] runMissedActivityGapAnalysis: workshop.modules is not an array — skipping gap analysis for', userEmail);
        return 0;
      }

      const sanitizedEmail = this.sanitizeEmail(userEmail);
      const workshopRef = doc(
        this.firestore,
        `partners/${partnerId}/workshops/${workshopId}`
      );

      let newMissedCount = 0;
      const updates: Record<string, unknown> = { updatedAt: Date.now() };
      const updatedModules = [...workshop.modules];
      let modulesChanged = false;

      for (let moduleIndex = 0; moduleIndex < workshop.modules.length; moduleIndex++) {
        const module = workshop.modules[moduleIndex];
        const userResponses = module.responses?.[userEmail] || {};
        const existingMissed = workshop.missedActivities?.[sanitizedEmail]?.[module.moduleId] || [];
        const newMissedForModule = [...existingMissed];

        for (const activity of module.activities) {
          // Only check activities that are marked as done (already presented in the workshop)
          if (!activity.done) continue;

          // Discussion-only activities are never "missed" — notes are optional
          const isAllDiscussion = activity.steps?.length > 0 &&
            activity.steps.every(s => s.type === 'group-discussion');
          if (isAllDiscussion) continue;

          const hasResponse = !!userResponses[activity.id];
          const alreadyTracked = existingMissed.includes(activity.id);

          if (!hasResponse && !alreadyTracked) {
            // User missed this activity entirely — no response at all
            newMissedForModule.push(activity.id);
            newMissedCount++;


            // Create a placeholder incomplete response
            const mod = updatedModules[moduleIndex];
            if (!mod.responses) mod.responses = {};
            if (!mod.responses[userEmail]) mod.responses[userEmail] = {};

            mod.responses[userEmail][activity.id] = {
              activityId: activity.id,
              completedAt: Date.now(),
              completionTime: 0,
              steps: activity.steps.map(step => ({
                stepId: step.id,
                prompt: step.prompt,
                answer: '',
                answeredAt: Date.now(),
                isIncomplete: true
              })),
              isIncomplete: true,
              incompletionReason: 'disconnected'
            };
            modulesChanged = true;
          }
        }

        // Update missed activities if new ones found for this module
        if (newMissedForModule.length > existingMissed.length) {
          updates[`missedActivities.${sanitizedEmail}.${module.moduleId}`] = newMissedForModule;
        }
      }

      if (newMissedCount > 0) {
        if (modulesChanged) {
          updates['modules'] = updatedModules;
        }
        await updateDoc(workshopRef, updates);
      } else {
      }

      return newMissedCount;
    } catch (error) {
      console.error('[WorkshopService] Error running gap analysis:', error);
      return 0;
    }
  }

  /**
   * Run gap analysis for ALL invited participants at workshop completion
   * Catches users who never reconnected after disconnecting
   * 
   * @param partnerId - Partner ID
   * @param workshopId - Workshop ID
   */
  async runCompletionGapAnalysis(
    partnerId: string,
    workshopId: string
  ): Promise<void> {
    try {
      const workshop = await this.getWorkshopById(partnerId, workshopId);
      if (!workshop) return;


      for (const userEmail of workshop.invitedParticipants) {
        const missed = await this.runMissedActivityGapAnalysis(
          partnerId,
          workshopId,
          userEmail
        );
        if (missed > 0) {
        }
      }

    } catch (error) {
      console.error('[WorkshopService] Error running completion gap analysis:', error);
    }
  }

  /**
   * Get missed activities for a user
   * Expands the simplified storage structure into full activity info for UI display
   * @param partnerId - Partner ID
   * @param workshopId - Workshop ID
   * @param userEmail - User's email
   * @returns Array of missed activity info with module context
   */
  async getMissedActivities(
    partnerId: string,
    workshopId: string,
    userEmail: string
  ): Promise<import('src/app/core/schema/Workshop.model').MissedActivityInfo[]> {
    try {
      const workshop = await this.getWorkshopById(partnerId, workshopId);
      if (!workshop) {
        return [];
      }
      
      const sanitizedEmail = this.sanitizeEmail(userEmail);
      const userMissed = workshop.missedActivities?.[sanitizedEmail] || {};
      
      const expandedList: import('src/app/core/schema/Workshop.model').MissedActivityInfo[] = [];
      
      // For each module with missed activities
      for (const [moduleId, activityIds] of Object.entries(userMissed)) {
        // Find the module in the workshop
        const moduleIndex = workshop.modules.findIndex(m => m.moduleId === moduleId);
        if (moduleIndex === -1) continue;
        
        const module = workshop.modules[moduleIndex];
        
        // For each missed activity, build the expanded info
        for (const activityId of activityIds) {
          const activity = module.activities.find(a => a.id === activityId);
          if (activity) {
            expandedList.push({
              activityId,
              moduleIndex,
              moduleId,
              timestamp: activity.timestamp
            });
          }
        }
      }
      
      return expandedList;
    } catch (error) {
      console.error('[WorkshopService] Error getting missed activities:', error);
      return [];
    }
  }

  // ============================================================================
  // Cross-Partner Workshops (Blume Admin)
  // ============================================================================

  /**
   * Load workshops across multiple partners (or all partners if none specified).
   * Used exclusively by the Blume Admin cross-partner workshops view.
   *
   * @param params.from  - Inclusive lower bound as a ms timestamp (optional)
   * @param params.to    - Inclusive upper bound as a ms timestamp (optional)
   * @param params.partnerIds - List of partner IDs to query; queries ALL partners if omitted/empty
   */
  async loadWorkshopsAcrossPartners(params: {
    from?: number;
    to?: number;
    partnerIds?: string[];
  }): Promise<(Workshop & { partnerId: string })[]> {
    const { from, to, partnerIds } = params;
    const idsToQuery =
      partnerIds && partnerIds.length > 0
        ? partnerIds
        : await this.getAllPartnerIds();

    const results = await Promise.all(
      idsToQuery.map(async (partnerId) => {
        try {
          const col = collection(this.firestore, `partners/${partnerId}/workshops`);
          const constraints: QueryConstraint[] = [];
          if (from != null) constraints.push(where('scheduledDate', '>=', from));
          if (to   != null) constraints.push(where('scheduledDate', '<=', to));
          constraints.push(orderBy('scheduledDate', 'asc'));
          const snap = await getDocs(query(col, ...constraints));
          return snap.docs.map(d => ({
            id: d.id,
            partnerId,
            ...(d.data() as Omit<Workshop, 'id'>),
          }));
        } catch (error) {
          console.error(`[WorkshopService] Error loading workshops for partner ${partnerId}:`, error);
          return [];
        }
      })
    );

    return results
      .flat()
      .sort((a, b) => (a.scheduledDate ?? 0) - (b.scheduledDate ?? 0));
  }

  private async getAllPartnerIds(): Promise<string[]> {
    try {
      const snap = await getDocs(collection(this.firestore, 'partners'));
      return snap.docs.map(d => d.id);
    } catch (error) {
      console.error('[WorkshopService] Error fetching all partner IDs:', error);
      return [];
    }
  }

  /**
   * Complete a missed activity (updates original response and removes from missed list)
   * @param partnerId - Partner ID
   * @param workshopId - Workshop ID
   * @param userEmail - User's email
   * @param activityId - Activity ID to complete
   * @param completeResponse - Complete activity response
   */
  async completeMissedActivity(
    partnerId: string,
    workshopId: string,
    userEmail: string,
    activityId: string,
    completeResponse: import('src/app/core/schema/Workshop.model').ActivityResponse
  ): Promise<void> {
    try {
      
      const workshop = await this.getWorkshopById(partnerId, workshopId);
      if (!workshop) {
        console.error('[WorkshopService] ❌ Workshop not found');
        throw new Error('Workshop not found');
      }
      
      // IMPORTANT: Two email formats are used:
      // - sanitizedEmail (dots -> underscores): used for missedActivities Firestore dot-notation paths
      // - userEmail (raw): used for module.responses keys (matches how submitActivityResponse saves them)
      const sanitizedEmail = this.sanitizeEmail(userEmail);
      
      const userMissed = workshop.missedActivities?.[sanitizedEmail] || {};
      
      // Find which module this activity belongs to
      let moduleId: string | null = null;
      let moduleIndex = -1;
      
      for (const [mId, activityIds] of Object.entries(userMissed)) {
        if (activityIds.includes(activityId)) {
          moduleId = mId;
          moduleIndex = workshop.modules.findIndex(m => m.moduleId === mId);
          break;
        }
      }
      
      if (moduleId === null || moduleIndex === -1) {
        console.error('[WorkshopService] ❌ Missed activity not found in workshop.missedActivities');
        throw new Error('Missed activity not found');
      }
      
      // Update the original response in the module
      const workshopRef = doc(
        this.firestore,
        `partners/${partnerId}/workshops/${workshopId}`
      );
      
      const updatedModules = [...workshop.modules];
      const module = updatedModules[moduleIndex];
      
      // Use RAW email for responses (matches submitActivityResponse which saves with raw email)
      
      // Ensure responses object exists
      if (!module.responses) {
        module.responses = {};
      }
      
      if (!module.responses[userEmail]) {
        module.responses[userEmail] = {};
      }
      
      // Update with complete response (remove incomplete flags)
      // Create a clean response object without incompletionReason
      const cleanResponse: ActivityResponse & { incompletionReason?: unknown } = {
        ...completeResponse,
        isIncomplete: false
      };

      // Remove incompletionReason if it exists (Firestore doesn't allow undefined)
      delete cleanResponse.incompletionReason;

      // Also clean up any undefined values in step responses
      if (cleanResponse.steps) {
        cleanResponse.steps = cleanResponse.steps.map(step => {
          const cleanStep: Record<string, unknown> = { ...step };
          // Remove any undefined fields
          Object.keys(cleanStep).forEach(key => {
            if (cleanStep[key] === undefined) {
              delete cleanStep[key];
            }
          });
          return cleanStep as unknown as typeof step;
        });
      }
      
      // Save under RAW email key (consistent with submitActivityResponse)
      module.responses[userEmail][activityId] = cleanResponse;
      
      
      // Remove from missed activities array for this module
      // Use SANITIZED email for missedActivities (Firestore dot-notation paths)
      const updatedActivitiesForModule = userMissed[moduleId].filter(id => id !== activityId);
      
      
      // Build the update object
      const updates: Record<string, unknown> = {
        modules: updatedModules,
        updatedAt: Date.now()
      };
      
      if (updatedActivitiesForModule.length === 0) {
        // Delete the module key entirely from missedActivities
        updates[`missedActivities.${sanitizedEmail}.${moduleId}`] = deleteField();
        
        // Check if this was the last module with missed activities for this user
        const remainingModules = Object.keys(userMissed).filter(mId => mId !== moduleId);
        if (remainingModules.length === 0) {
          // Also delete the user's entry entirely from missedActivities
          updates[`missedActivities.${sanitizedEmail}`] = deleteField();
        }
      } else {
        // Update with filtered array
        updates[`missedActivities.${sanitizedEmail}.${moduleId}`] = updatedActivitiesForModule;
      }
      
      
      await updateDoc(workshopRef, updates);
      
    } catch (error) {
      console.error('[WorkshopService] Error completing missed activity:', error);
      throw error;
    }
  }
}
